import { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { PortalLayout } from '../components/PortalLayout';

/* ── Types ──────────────────────────────────────────────────────────── */

interface FleetVehicle {
  id: number;
  registration: string;
  vin?: string;
  make?: string;
  model?: string;
  year?: number;
  gross_weight_kg?: number;
  mot_due?: string;
  tax_due?: string;
  tacho_calibration_due?: string;
  assigned_driver_id?: number;
  assigned_driver_name?: string;
  is_active: boolean;
  notes?: string;
  pmi_interval_weeks?: number;
  tractor_height?: number;
  tractor_height_unit?: string;
  vehicle_type?: string;
  is_trailer?: boolean;
  has_alt_fuel?: boolean;
  last_pmi_date?: string;
  next_pmi_due?: string;
  last_brake_test_date?: string;
  insurance_due?: string;
  speed_limiter_due?: string;
  last_known_odometer_km?: number;
  operator_licence_disc?: string;
}

interface TrackingVehicle {
  registration: string;
  status: string;
  last_event: string;
  event_date?: string;
  latitude?: number;
  longitude?: number;
  location?: string;
  poi_name?: string;
  speed_kph?: number;
  fuel_level?: number;
  odometer_km?: number;
  engine_hours?: number;
  driver_name?: string;
  state_duration_seconds?: number;
  battery_level?: number;
}

interface DriverOption {
  id: number;
  first_names: string;
  surname: string;
}

interface ActivityRecord {
  driver_id: number;
  driver_name: string;
  driver_card_number?: string;
  first_use?: string;
  last_use?: string;
  date?: string;
  odometer_start_km?: number;
  odometer_end_km?: number;
  distance_km?: number;
  driving_hours?: number;
  card_download_id?: number;
  download_date?: string;
}

interface PMIRecord {
  id: number;
  vehicle_id: number;
  scheduled_date: string;
  completed_date?: string;
  status: string;
  inspection_type: string;
  odometer_km?: number;
  inspector_name?: string;
  workshop?: string;
  defects_found?: string;
  rectification_notes?: string;
  rectification_date?: string;
  next_due_date?: string;
  cost?: number;
  notes?: string;
  created_at?: string;
}

const EMPTY_FORM: Record<string, string> = {
  registration: '', vin: '', make: '', model: '', year: '',
  gross_weight_kg: '', mot_due: '', tax_due: '', tacho_calibration_due: '',
  assigned_driver_id: '', notes: '', pmi_interval_weeks: '6',
  next_pmi_due: '', insurance_due: '', speed_limiter_due: '',
  operator_licence_disc: '',
  tractor_height: '', tractor_height_unit: 'metric',
  vehicle_type: 'rigid', is_trailer: 'false', has_alt_fuel: 'false',
};

/* ── Helpers ─────────────────────────────────────────────────────────── */

function isDueSoon(dateStr?: string): 'ok' | 'warning' | 'overdue' | 'none' {
  if (!dateStr) return 'none';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (d.getTime() - now.getTime()) / 86400000;
  if (diff < 0) return 'overdue';
  if (diff < 30) return 'warning';
  return 'ok';
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function complianceStatus(v: FleetVehicle): 'overdue' | 'warning' | 'ok' {
  const checks = [v.mot_due, v.tax_due, v.tacho_calibration_due, v.next_pmi_due, v.insurance_due, v.speed_limiter_due];
  let worst: 'ok' | 'warning' | 'overdue' = 'ok';
  for (const d of checks) {
    const s = isDueSoon(d);
    if (s === 'overdue') return 'overdue';
    if (s === 'warning') worst = 'warning';
  }
  return worst;
}

function daysUntil(dateStr?: string): string {
  if (!dateStr) return '';
  const diff = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff === 0) return 'Today';
  return `${diff}d`;
}

/* ── Date cell for compliance table ──────────────────────────────────── */
function DateCell({ date }: { date?: string }) {
  const s = isDueSoon(date);
  if (s === 'none') return <span className="text-gray-300">—</span>;
  const base = 'text-xs';
  if (s === 'overdue') return (
    <span className={`${base} text-red-700 font-semibold`}>
      {formatDate(date)} <span className="text-red-500 text-[10px] ml-1">{daysUntil(date)}</span>
    </span>
  );
  if (s === 'warning') return (
    <span className={`${base} text-amber-700 font-medium`}>
      {formatDate(date)} <span className="text-amber-500 text-[10px] ml-1">{daysUntil(date)}</span>
    </span>
  );
  return <span className={`${base} text-gray-700`}>{formatDate(date)}</span>;
}

/* ── Status indicator ─────────────────────────────────────────────────── */
function StatusIndicator({ status }: { status: 'overdue' | 'warning' | 'ok' }) {
  if (status === 'overdue') return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-700">
      <span className="w-2 h-2 rounded-full bg-red-500" />Overdue
    </span>
  );
  if (status === 'warning') return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700">
      <span className="w-2 h-2 rounded-full bg-amber-400" />Due Soon
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-green-700">
      <span className="w-2 h-2 rounded-full bg-green-500" />Compliant
    </span>
  );
}

/* ── Main Component ──────────────────────────────────────────────────── */

export function FleetPage() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState<FleetVehicle[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'overdue' | 'warning' | 'ok'>('all');
  const [sortCol, setSortCol] = useState<string>('registration');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Detail view
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detailTab, setDetailTab] = useState<'compliance' | 'activity' | 'pmi'>('compliance');
  const [activity, setActivity] = useState<ActivityRecord[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [pmiRecords, setPmiRecords] = useState<PMIRecord[]>([]);
  const [pmiLoading, setPmiLoading] = useState(false);

  // Inactive vehicles
  const [inactiveVehicles, setInactiveVehicles] = useState<FleetVehicle[]>([]);
  const [showInactive, setShowInactive] = useState(false);

  // Live tracking data
  const [trackingMap, setTrackingMap] = useState<Record<string, TrackingVehicle>>({});

  // Forms
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // PMI scheduling
  const [showPmiForm, setShowPmiForm] = useState(false);
  const [pmiForm, setPmiForm] = useState({ scheduled_date: '', inspection_type: 'PMI', inspector_name: '', workshop: '', notes: '' });
  const [pmiSaving, setPmiSaving] = useState(false);

  const isManager = user?.role === 'admin' || user?.role === 'transport_manager';
  const selected = vehicles.find(v => v.id === selectedId) || null;

  // Helper to get tracking data for a fleet vehicle
  function getTracking(v: FleetVehicle): TrackingVehicle | null {
    const key = (v.registration || '').toUpperCase().replace(/[\s-]/g, '');
    return trackingMap[key] || null;
  }

  function fmtDuration(secs?: number | null): string {
    if (!secs || secs < 60) return '';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  }

  function statusLabel(s?: string): string {
    if (!s) return '';
    const map: Record<string, string> = {
      driving: 'Driving', idle: 'Idle', stopped: 'Stopped',
      engine_on: 'Engine On', unknown: 'Offline',
    };
    return map[s] || s;
  }

  function statusColor(s?: string): string {
    if (s === 'driving') return 'text-green-700 bg-green-50';
    if (s === 'idle') return 'text-amber-700 bg-amber-50';
    if (s === 'stopped') return 'text-gray-600 bg-gray-100';
    if (s === 'engine_on') return 'text-blue-700 bg-blue-50';
    return 'text-gray-400 bg-gray-50';
  }

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (selectedId && detailTab === 'activity') loadActivity(selectedId);
    if (selectedId && detailTab === 'pmi') loadPMIs(selectedId);
  }, [selectedId, detailTab]);

  async function loadData() {
    setLoadError('');
    try {
      const [v, d] = await Promise.all([api.getVehicles(), api.getDrivers({ page_size: 500 })]);
      setVehicles(v);
      setDrivers(Array.isArray(d) ? d : d.drivers || []);
      // Load inactive vehicles + live tracking data (non-blocking)
      try {
        const [inactive, trackingResp] = await Promise.all([
          api.getInactiveVehicles().catch(() => []),
          api.getTrackingVehicles().catch(() => ({ vehicles: [] })),
        ]);
        setInactiveVehicles(inactive);
        // Build lookup by normalised registration
        const tMap: Record<string, TrackingVehicle> = {};
        for (const tv of (trackingResp?.vehicles || [])) {
          const key = (tv.registration || '').toUpperCase().replace(/[\s-]/g, '');
          if (key) tMap[key] = tv;
        }
        setTrackingMap(tMap);
      } catch { /* non-critical */ }
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  }

  async function loadActivity(vehicleId: number) {
    setActivityLoading(true);
    try {
      const data = await api.getVehicleActivity(vehicleId);
      setActivity(data.activity || []);
    } catch { setActivity([]); }
    finally { setActivityLoading(false); }
  }

  async function loadPMIs(vehicleId: number) {
    setPmiLoading(true);
    try {
      const data = await api.getVehiclePMIs(vehicleId);
      setPmiRecords(data);
    } catch { setPmiRecords([]); }
    finally { setPmiLoading(false); }
  }

  function openAdd() {
    setForm({ ...EMPTY_FORM }); setEditId(null); setShowForm(true); setError('');
  }

  function openEdit(v: FleetVehicle) {
    setForm({
      registration: v.registration, vin: v.vin || '', make: v.make || '', model: v.model || '',
      year: v.year ? String(v.year) : '', gross_weight_kg: v.gross_weight_kg ? String(v.gross_weight_kg) : '',
      mot_due: v.mot_due || '', tax_due: v.tax_due || '', tacho_calibration_due: v.tacho_calibration_due || '',
      assigned_driver_id: v.assigned_driver_id ? String(v.assigned_driver_id) : '', notes: v.notes || '',
      pmi_interval_weeks: v.pmi_interval_weeks ? String(v.pmi_interval_weeks) : '6',
      next_pmi_due: v.next_pmi_due || '', insurance_due: v.insurance_due || '',
      speed_limiter_due: v.speed_limiter_due || '', operator_licence_disc: v.operator_licence_disc || '',
      tractor_height: v.tractor_height ? String(v.tractor_height) : '',
      tractor_height_unit: v.tractor_height_unit || 'metric',
      vehicle_type: v.vehicle_type || 'rigid', is_trailer: v.is_trailer ? 'true' : 'false',
      has_alt_fuel: v.has_alt_fuel ? 'true' : 'false',
    });
    setEditId(v.id); setShowForm(true); setError('');
  }

  async function handleSave() {
    if (!form.registration.trim()) { setError('Registration is required'); return; }
    setSaving(true); setError('');
    try {
      const payload: any = {
        registration: form.registration, vin: form.vin || null, make: form.make || null,
        model: form.model || null, year: form.year ? parseInt(form.year) : null,
        gross_weight_kg: form.gross_weight_kg ? parseInt(form.gross_weight_kg) : null,
        mot_due: form.mot_due || null, tax_due: form.tax_due || null,
        tacho_calibration_due: form.tacho_calibration_due || null,
        assigned_driver_id: form.assigned_driver_id ? parseInt(form.assigned_driver_id) : null,
        notes: form.notes || null, pmi_interval_weeks: form.pmi_interval_weeks ? parseInt(form.pmi_interval_weeks) : 6,
        next_pmi_due: form.next_pmi_due || null, insurance_due: form.insurance_due || null,
        speed_limiter_due: form.speed_limiter_due || null, operator_licence_disc: form.operator_licence_disc || null,
        tractor_height: form.tractor_height ? parseFloat(form.tractor_height) : null,
        tractor_height_unit: form.tractor_height_unit || 'metric',
        vehicle_type: form.vehicle_type || 'rigid', is_trailer: form.is_trailer === 'true',
        has_alt_fuel: form.has_alt_fuel === 'true',
      };
      if (editId) { await api.updateVehicle(editId, payload); }
      else { await api.createVehicle(payload); }
      setShowForm(false); loadData();
    } catch (err: any) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleDeactivate(id: number) {
    if (!confirm('Deactivate this vehicle? It will be moved to the inactive list and can be reactivated later.')) return;
    try { await api.deleteVehicle(id); if (selectedId === id) setSelectedId(null); loadData(); }
    catch (err: any) { setError(err.message); }
  }

  async function handlePermanentDelete(id: number) {
    if (!confirm('PERMANENTLY delete this vehicle and all associated records? This cannot be undone.')) return;
    if (!confirm('Final confirmation — are you sure?')) return;
    try { await api.permanentDeleteVehicle(id); if (selectedId === id) setSelectedId(null); loadData(); }
    catch (err: any) { setError(err.message); }
  }

  async function handleReactivate(id: number) {
    try { await api.reactivateVehicle(id); loadData(); }
    catch (err: any) { setError(err.message); }
  }

  async function handleSchedulePMI() {
    if (!selectedId || !pmiForm.scheduled_date) return;
    setPmiSaving(true);
    try {
      await api.createPMI(selectedId, pmiForm);
      setShowPmiForm(false);
      setPmiForm({ scheduled_date: '', inspection_type: 'PMI', inspector_name: '', workshop: '', notes: '' });
      loadPMIs(selectedId);
    } catch (err: any) { setError(err.message); }
    finally { setPmiSaving(false); }
  }

  async function handleCompletePMI(pmiId: number) {
    try {
      await api.updatePMI(pmiId, { completed_date: new Date().toISOString().split('T')[0], status: 'completed' });
      if (selectedId) loadPMIs(selectedId); loadData();
    } catch (err: any) { setError(err.message); }
  }

  async function handleDeletePMI(pmiId: number) {
    if (!confirm('Delete this inspection record?')) return;
    try { await api.deletePMI(pmiId); if (selectedId) loadPMIs(selectedId); }
    catch (err: any) { setError(err.message); }
  }

  /* ── Sorting ──────────────────────────────────────────────────────── */
  function toggleSort(col: string) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  const SortIcon = ({ col }: { col: string }) => {
    if (sortCol !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-gray-600 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  /* ── Filter & sort vehicles ──────────────────────────────────────── */
  const filtered = vehicles
    .filter(v => {
      if (filterStatus !== 'all' && complianceStatus(v) !== filterStatus) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return v.registration.toLowerCase().includes(s) ||
        (v.make || '').toLowerCase().includes(s) ||
        (v.model || '').toLowerCase().includes(s) ||
        (v.assigned_driver_name || '').toLowerCase().includes(s);
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const av = (a as any)[sortCol] ?? '';
      const bv = (b as any)[sortCol] ?? '';
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return ((av || 0) - (bv || 0)) * dir;
    });

  /* ── Summary counts ──────────────────────────────────────────────── */
  const overdueCount = vehicles.filter(v => complianceStatus(v) === 'overdue').length;
  const warningCount = vehicles.filter(v => complianceStatus(v) === 'warning').length;
  const compliantCount = vehicles.filter(v => complianceStatus(v) === 'ok').length;

  /* ── Render ─────────────────────────────────────────────────────────── */

  if (selectedId && selected) {
    return (
      <PortalLayout currentPage="fleet">
        <div className="h-[calc(100vh-64px)] overflow-y-auto bg-gray-50">
          {/* Vehicle detail header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button onClick={() => setSelectedId(null)} className="text-sm text-gray-500 hover:text-gray-800">
                  ← Fleet List
                </button>
                <div className="h-5 w-px bg-gray-200" />
                <div className="bg-yellow-300 border-2 border-gray-700 rounded px-3 py-1">
                  <span className="font-mono font-bold text-gray-900 text-base tracking-widest">{selected.registration}</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {[selected.make, selected.model, selected.year].filter(Boolean).join(' ') || 'No details'}
                  </div>
                  {selected.vin && <div className="text-xs text-gray-400 font-mono">{selected.vin}</div>}
                </div>
                <StatusIndicator status={complianceStatus(selected)} />
              </div>
              {isManager && (
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(selected)} className="px-3 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800">Edit</button>
                  <button onClick={() => handleDeactivate(selected.id)} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded hover:bg-gray-50">Deactivate</button>
                  <button onClick={() => handlePermanentDelete(selected.id)} className="px-3 py-1.5 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">Delete</button>
                </div>
              )}
            </div>

            {/* Vehicle summary row */}
            <div className="flex gap-8 mt-3 text-xs text-gray-500">
              {selected.vehicle_type && <span>Type: <span className="text-gray-700 font-medium capitalize">{selected.vehicle_type}</span></span>}
              {(getTracking(selected)?.driver_name || selected.assigned_driver_name) && <span>Driver: <span className="text-gray-700 font-medium">{getTracking(selected)?.driver_name || selected.assigned_driver_name}</span></span>}
              {selected.gross_weight_kg && <span>GVW: <span className="text-gray-700 font-medium">{(selected.gross_weight_kg / 1000).toFixed(1)}t</span></span>}
              {selected.operator_licence_disc && <span>O-Licence: <span className="text-gray-700 font-medium">{selected.operator_licence_disc}</span></span>}
              {selected.pmi_interval_weeks && <span>PMI Interval: <span className="text-gray-700 font-medium">{selected.pmi_interval_weeks} wk</span></span>}
            </div>

            {/* Live tracking data bar */}
            {(() => {
              const tv = getTracking(selected);
              if (!tv) return null;
              return (
                <div className="mt-3 flex items-center gap-6 bg-gray-50 rounded px-4 py-2.5 border border-gray-200 text-xs">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded font-medium ${statusColor(tv.status)}`}>{statusLabel(tv.status)}</span>
                    {tv.state_duration_seconds && tv.state_duration_seconds > 60 && (
                      <span className="text-gray-400">for {fmtDuration(tv.state_duration_seconds)}</span>
                    )}
                  </div>
                  <div className="h-3 w-px bg-gray-300" />
                  {(tv.poi_name || tv.location) && (
                    <>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-400">Location:</span>
                        <span className="text-gray-700 font-medium">{tv.poi_name || ''}{tv.poi_name && tv.location ? ', ' : ''}{tv.location || ''}</span>
                      </div>
                      <div className="h-3 w-px bg-gray-300" />
                    </>
                  )}
                  {tv.odometer_km ? (
                    <>
                      <div>
                        <span className="text-gray-400">Mileage: </span>
                        <span className="text-gray-700 font-medium">{Math.round(tv.odometer_km * 0.621371).toLocaleString()} mi</span>
                        <span className="text-gray-300 ml-1">({Math.round(tv.odometer_km).toLocaleString()} km)</span>
                      </div>
                      <div className="h-3 w-px bg-gray-300" />
                    </>
                  ) : null}
                  {tv.fuel_level != null && (
                    <>
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-400">Fuel:</span>
                        <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${tv.fuel_level < 20 ? 'bg-red-500' : tv.fuel_level < 40 ? 'bg-amber-400' : 'bg-green-500'}`}
                            style={{ width: `${Math.min(100, tv.fuel_level)}%` }} />
                        </div>
                        <span className={`font-medium ${tv.fuel_level < 20 ? 'text-red-600' : 'text-gray-700'}`}>{tv.fuel_level}%</span>
                      </div>
                      <div className="h-3 w-px bg-gray-300" />
                    </>
                  )}
                  {tv.speed_kph != null && tv.speed_kph > 0 && (
                    <>
                      <div>
                        <span className="text-gray-400">Speed: </span>
                        <span className="text-gray-700 font-medium">{Math.round(tv.speed_kph * 0.621371)} mph</span>
                      </div>
                      <div className="h-3 w-px bg-gray-300" />
                    </>
                  )}
                  {tv.engine_hours ? (
                    <div>
                      <span className="text-gray-400">Engine: </span>
                      <span className="text-gray-700 font-medium">{tv.engine_hours.toLocaleString()} hrs</span>
                    </div>
                  ) : null}
                </div>
              );
            })()}

            {/* Detail tabs */}
            <div className="flex gap-0 mt-4 -mb-4">
              {(['compliance', 'activity', 'pmi'] as const).map(tab => (
                <button key={tab} onClick={() => setDetailTab(tab)}
                  className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${detailTab === tab
                    ? 'border-slate-700 text-slate-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                >
                  {tab === 'compliance' ? 'Compliance Dates' : tab === 'activity' ? 'Driver Activity' : 'Inspections / PMI'}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div className="p-6 max-w-5xl">

            {/* ── Compliance Dates ───────────────────────────────────── */}
            {detailTab === 'compliance' && (
              <div className="bg-white rounded border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-500 uppercase w-48">Item</th>
                      <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-500 uppercase">Due Date</th>
                      <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-500 uppercase w-28">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { label: 'MOT', date: selected.mot_due },
                      { label: 'Road Tax', date: selected.tax_due },
                      { label: 'Tachograph Calibration', date: selected.tacho_calibration_due },
                      { label: 'Next PMI', date: selected.next_pmi_due },
                      { label: 'Insurance', date: selected.insurance_due },
                      { label: 'Speed Limiter', date: selected.speed_limiter_due },
                      { label: 'Last PMI Completed', date: selected.last_pmi_date },
                      { label: 'Last Brake Test', date: selected.last_brake_test_date },
                    ].map((item, i) => {
                      const s = isDueSoon(item.date);
                      return (
                        <tr key={i} className={s === 'overdue' ? 'bg-red-50/40' : s === 'warning' ? 'bg-amber-50/30' : ''}>
                          <td className="px-5 py-3 text-sm text-gray-700">{item.label}</td>
                          <td className="px-5 py-3"><DateCell date={item.date} /></td>
                          <td className="px-5 py-3">
                            {s === 'none' ? <span className="text-xs text-gray-300">Not set</span>
                              : s === 'overdue' ? <span className="text-xs font-semibold text-red-600">OVERDUE</span>
                              : s === 'warning' ? <span className="text-xs font-medium text-amber-600">DUE SOON</span>
                              : <span className="text-xs text-green-600">OK</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {selected.notes && (
                  <div className="border-t border-gray-200 px-5 py-3">
                    <span className="text-xs font-medium text-gray-500">Notes: </span>
                    <span className="text-sm text-gray-600">{selected.notes}</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Driver Activity ────────────────────────────────────── */}
            {detailTab === 'activity' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Driver Usage History</h3>
                  <span className="text-xs text-gray-400">From tachograph card data</span>
                </div>

                {activityLoading ? (
                  <div className="bg-white rounded border border-gray-200 p-8 text-center text-gray-400 text-sm">Loading...</div>
                ) : activity.length === 0 ? (
                  <div className="bg-white rounded border border-gray-200 p-8 text-center text-gray-400 text-sm">
                    No driver activity found for this vehicle.
                  </div>
                ) : (
                  <div className="bg-white rounded border border-gray-200 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Driver</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Date / Period</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Distance</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Odometer</th>
                          <th className="text-center px-4 py-2.5 text-xs font-medium text-gray-500 uppercase w-16"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {activity.map((a, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5">
                              <div className="text-sm text-gray-900">{a.driver_name}</div>
                              {a.driver_card_number && <div className="text-xs text-gray-400 font-mono">{a.driver_card_number}</div>}
                            </td>
                            <td className="px-4 py-2.5">
                              {a.first_use ? (
                                <div>
                                  <div className="text-xs text-gray-700">{formatDateTime(a.first_use)}</div>
                                  {a.last_use && a.last_use !== a.first_use && (
                                    <div className="text-xs text-gray-400">to {formatDateTime(a.last_use)}</div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-xs text-gray-700">{formatDate(a.date)}</div>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right text-sm text-gray-700">
                              {a.distance_km ? `${a.distance_km.toLocaleString()} km` : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-right text-xs text-gray-500">
                              {a.odometer_start_km && a.odometer_end_km
                                ? `${a.odometer_start_km.toLocaleString()} → ${a.odometer_end_km.toLocaleString()}`
                                : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              {a.driver_id && (
                                <a href={`/tacho?driver=${a.driver_id}`}
                                  className="text-xs text-blue-600 hover:text-blue-800">View</a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── Inspections / PMI ──────────────────────────────────── */}
            {detailTab === 'pmi' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Inspection Records</h3>
                  {isManager && (
                    <button onClick={() => setShowPmiForm(true)}
                      className="px-3 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800">
                      + Schedule Inspection
                    </button>
                  )}
                </div>

                {/* PMI scheduling form */}
                {showPmiForm && (
                  <div className="bg-white rounded border border-gray-200 p-4 mb-4">
                    <h4 className="text-xs font-medium text-gray-700 uppercase mb-3">Schedule Inspection</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Date *</label>
                        <input type="date" value={pmiForm.scheduled_date}
                          onChange={(e) => setPmiForm({ ...pmiForm, scheduled_date: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Type</label>
                        <select value={pmiForm.inspection_type}
                          onChange={(e) => setPmiForm({ ...pmiForm, inspection_type: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-white">
                          <option value="PMI">PMI</option>
                          <option value="MOT">MOT</option>
                          <option value="Tacho Calibration">Tacho Calibration</option>
                          <option value="Brake Test">Brake Test</option>
                          <option value="Speed Limiter">Speed Limiter</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Inspector</label>
                        <input type="text" value={pmiForm.inspector_name}
                          onChange={(e) => setPmiForm({ ...pmiForm, inspector_name: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Workshop</label>
                        <input type="text" value={pmiForm.workshop}
                          onChange={(e) => setPmiForm({ ...pmiForm, workshop: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-xs text-gray-500 mb-1">Notes</label>
                      <textarea value={pmiForm.notes}
                        onChange={(e) => setPmiForm({ ...pmiForm, notes: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded" rows={2} />
                    </div>
                    <div className="flex justify-end gap-2 mt-3">
                      <button onClick={() => setShowPmiForm(false)} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                      <button onClick={handleSchedulePMI} disabled={pmiSaving || !pmiForm.scheduled_date}
                        className="px-4 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50">
                        {pmiSaving ? 'Saving...' : 'Schedule'}
                      </button>
                    </div>
                  </div>
                )}

                {pmiLoading ? (
                  <div className="bg-white rounded border border-gray-200 p-8 text-center text-gray-400 text-sm">Loading...</div>
                ) : pmiRecords.length === 0 ? (
                  <div className="bg-white rounded border border-gray-200 p-8 text-center text-gray-400 text-sm">
                    No inspection records. Use the button above to schedule one.
                  </div>
                ) : (
                  <div className="bg-white rounded border border-gray-200 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Scheduled</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Completed</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Inspector</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Workshop</th>
                          {isManager && <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase w-32">Actions</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pmiRecords.map((r) => (
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5">
                              <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                r.status === 'completed' ? 'bg-green-50 text-green-700'
                                : r.status === 'overdue' ? 'bg-red-50 text-red-700'
                                : r.status === 'scheduled' ? 'bg-blue-50 text-blue-700'
                                : 'bg-gray-50 text-gray-500'
                              }`}>
                                {r.status.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-sm text-gray-900">{r.inspection_type}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-700">{formatDate(r.scheduled_date)}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-700">{r.completed_date ? formatDate(r.completed_date) : '—'}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-600">{r.inspector_name || '—'}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-600">{r.workshop || '—'}</td>
                            {isManager && (
                              <td className="px-4 py-2.5 text-right">
                                <div className="flex justify-end gap-3">
                                  {r.status === 'scheduled' && (
                                    <button onClick={() => handleCompletePMI(r.id)} className="text-xs text-green-600 hover:text-green-800">Complete</button>
                                  )}
                                  <button onClick={() => handleDeletePMI(r.id)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Defects & rectification shown below the table */}
                {pmiRecords.filter(r => r.defects_found).length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Defect Notes</h4>
                    <div className="space-y-2">
                      {pmiRecords.filter(r => r.defects_found).map(r => (
                        <div key={r.id} className="bg-white rounded border border-gray-200 px-4 py-3">
                          <div className="flex items-center gap-3 text-xs text-gray-500 mb-1">
                            <span className="font-medium text-gray-700">{r.inspection_type}</span>
                            <span>{formatDate(r.scheduled_date)}</span>
                          </div>
                          <p className="text-sm text-red-700">{r.defects_found}</p>
                          {r.rectification_notes && (
                            <p className="text-sm text-green-700 mt-1">Rectified: {r.rectification_notes}{r.rectification_date && ` (${formatDate(r.rectification_date)})`}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit modal (shared) */}
        {showForm && renderFormModal()}
      </PortalLayout>
    );
  }

  /* ── Main fleet list view ──────────────────────────────────────────── */

  function renderFormModal() {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white rounded shadow-lg w-full max-w-2xl mx-4 p-5 max-h-[90vh] overflow-y-auto">
          <h2 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-4">{editId ? 'Edit Vehicle' : 'Add Vehicle'}</h2>
          {error && <div className="bg-red-50 text-red-700 px-3 py-2 rounded mb-4 text-sm">{error}</div>}

          <div className="space-y-4">
            {/* Basic details */}
            <div>
              <h3 className="text-xs font-medium text-gray-400 uppercase mb-2">Vehicle Details</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Registration *</label>
                  <input type="text" value={form.registration} onChange={(e) => setForm({ ...form, registration: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded uppercase" placeholder="AB12 CDE" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">VIN</label>
                  <input type="text" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded font-mono" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 mt-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Make</label>
                  <input type="text" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded" placeholder="DAF" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Model</label>
                  <input type="text" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded" placeholder="XF 480" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Year</label>
                  <input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">GVW (kg)</label>
                  <input type="number" value={form.gross_weight_kg} onChange={(e) => setForm({ ...form, gross_weight_kg: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Assigned Driver</label>
                  <select value={form.assigned_driver_id} onChange={(e) => setForm({ ...form, assigned_driver_id: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-white">
                    <option value="">— None —</option>
                    {drivers.map(d => (
                      <option key={d.id} value={d.id}>{d.first_names} {d.surname}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">O-Licence Disc</label>
                  <input type="text" value={form.operator_licence_disc} onChange={(e) => setForm({ ...form, operator_licence_disc: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                </div>
              </div>
            </div>

            {/* Vehicle Configuration */}
            <div>
              <h3 className="text-xs font-medium text-gray-400 uppercase mb-2">Configuration</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Vehicle Type</label>
                  <select value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-white">
                    <option value="rigid">Rigid</option>
                    <option value="artic">Artic</option>
                    <option value="drawbar">Drawbar</option>
                    <option value="van">Van</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Height</label>
                  <div className="flex gap-1">
                    <input type="number" step="0.01" value={form.tractor_height} onChange={(e) => setForm({ ...form, tractor_height: e.target.value })}
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded" />
                    <select value={form.tractor_height_unit} onChange={(e) => setForm({ ...form, tractor_height_unit: e.target.value })}
                      className="w-16 px-1 py-2 text-xs border border-gray-200 rounded bg-white">
                      <option value="metric">m</option>
                      <option value="imperial">ft</option>
                    </select>
                  </div>
                </div>
                <div className="flex flex-col justify-center space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={form.is_trailer === 'true'} onChange={(e) => setForm({ ...form, is_trailer: e.target.checked ? 'true' : 'false' })}
                      className="rounded border-gray-300" />
                    Trailer unit
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={form.has_alt_fuel === 'true'} onChange={(e) => setForm({ ...form, has_alt_fuel: e.target.checked ? 'true' : 'false' })}
                      className="rounded border-gray-300" />
                    Alt fuel
                  </label>
                </div>
              </div>
            </div>

            {/* Compliance dates */}
            <div>
              <h3 className="text-xs font-medium text-gray-400 uppercase mb-2">Compliance Dates</h3>
              <div className="grid grid-cols-3 gap-3">
                {[
                  ['MOT Due', 'mot_due'], ['Tax Due', 'tax_due'], ['Tacho Calibration', 'tacho_calibration_due'],
                  ['Next PMI', 'next_pmi_due'], ['Insurance', 'insurance_due'], ['Speed Limiter', 'speed_limiter_due'],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-500 mb-1">{label}</label>
                    <input type="date" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <label className="block text-xs text-gray-500 mb-1">PMI Interval (weeks)</label>
                <input type="number" value={form.pmi_interval_weeks} onChange={(e) => setForm({ ...form, pmi_interval_weeks: e.target.value })}
                  className="w-32 px-3 py-2 text-sm border border-gray-200 rounded" min="1" max="13" />
                <span className="text-xs text-gray-400 ml-2">6–13 weeks typical</span>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded" rows={2} />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="px-6 py-2 text-sm bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50">
              {saving ? 'Saving...' : editId ? 'Update' : 'Add Vehicle'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PortalLayout currentPage="fleet">
      <div className="h-[calc(100vh-64px)] flex flex-col bg-gray-50">

        {/* ── Top Bar ──────────────────────────────────────────────── */}
        <div className="bg-white border-b border-gray-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <h1 className="text-sm font-semibold uppercase tracking-wide text-gray-800">Fleet Register</h1>
              <div className="flex items-center gap-1 text-xs">
                <span className="text-gray-400">{vehicles.length} vehicles</span>
                {overdueCount > 0 && (
                  <>
                    <span className="text-gray-300 mx-1">|</span>
                    <span className="text-red-600 font-medium">{overdueCount} overdue</span>
                  </>
                )}
                {warningCount > 0 && (
                  <>
                    <span className="text-gray-300 mx-1">|</span>
                    <span className="text-amber-600 font-medium">{warningCount} due soon</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Search registration, make, driver..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-64 px-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-slate-400 focus:border-slate-400"
              />
              <div className="flex items-center border border-gray-200 rounded overflow-hidden text-xs">
                {(['all', 'overdue', 'warning', 'ok'] as const).map(f => (
                  <button key={f} onClick={() => setFilterStatus(f)}
                    className={`px-3 py-1.5 ${filterStatus === f ? 'bg-slate-700 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
                    {f === 'all' ? 'All' : f === 'overdue' ? 'Overdue' : f === 'warning' ? 'Due Soon' : 'OK'}
                  </button>
                ))}
              </div>
              {isManager && (
                <button onClick={openAdd} className="px-4 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800">
                  + Add Vehicle
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Errors */}
        {loadError && (
          <div className="mx-6 mt-3 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm flex items-center justify-between">
            <span>{loadError}</span>
            <button onClick={() => { setLoadError(''); setLoading(true); loadData(); }} className="text-red-800 underline text-xs">Retry</button>
          </div>
        )}
        {error && <div className="mx-6 mt-3 bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}

        {/* ── Fleet Table ──────────────────────────────────────────── */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {loading ? (
            <div className="text-center text-gray-400 text-sm py-16">Loading fleet data...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-16">
              {vehicles.length === 0 ? 'No vehicles registered.' : 'No vehicles match your search.'}
            </div>
          ) : (
            <div className="bg-white rounded border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700" onClick={() => toggleSort('registration')}>
                      Registration<SortIcon col="registration" />
                    </th>
                    <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700" onClick={() => toggleSort('make')}>
                      Vehicle<SortIcon col="make" />
                    </th>
                    <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 uppercase">Driver</th>
                    <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 uppercase">Live Status</th>
                    <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 uppercase">Mileage</th>
                    <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 uppercase">Fuel</th>
                    <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700" onClick={() => toggleSort('mot_due')}>
                      MOT<SortIcon col="mot_due" />
                    </th>
                    <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700" onClick={() => toggleSort('next_pmi_due')}>
                      PMI<SortIcon col="next_pmi_due" />
                    </th>
                    <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500 uppercase w-24">Compliance</th>
                    {isManager && <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 uppercase w-24">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((v) => {
                    const cStatus = complianceStatus(v);
                    const tv = getTracking(v);
                    return (
                      <tr key={v.id}
                        className={`hover:bg-gray-50 cursor-pointer ${cStatus === 'overdue' ? 'bg-red-50/30' : ''}`}
                        onClick={() => { setSelectedId(v.id); setDetailTab('compliance'); }}>
                        <td className="px-3 py-2.5">
                          <div className="bg-yellow-300 border border-gray-700 rounded px-1.5 py-0.5 inline-block">
                            <span className="font-mono font-bold text-gray-900 text-xs tracking-wider">{v.registration}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="text-sm text-gray-900">{[v.make, v.model].filter(Boolean).join(' ') || '—'}</div>
                          <div className="text-xs text-gray-400">{[v.year, v.gross_weight_kg ? `${(v.gross_weight_kg / 1000).toFixed(1)}t` : null].filter(Boolean).join(' · ')}</div>
                        </td>
                        <td className="px-3 py-2.5 text-sm text-gray-600">
                          {tv?.driver_name || v.assigned_driver_name || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-3 py-2.5">
                          {tv ? (
                            <div>
                              <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${statusColor(tv.status)}`}>
                                {statusLabel(tv.status)}
                              </span>
                              {tv.state_duration_seconds && tv.state_duration_seconds > 60 ? (
                                <div className="text-[10px] text-gray-400 mt-0.5">{fmtDuration(tv.state_duration_seconds)}</div>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">No tracker</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          {tv?.poi_name || tv?.location ? (
                            <div>
                              {tv.poi_name && <div className="text-xs text-gray-800 font-medium truncate max-w-[180px]">{tv.poi_name}</div>}
                              {tv.location && <div className="text-[10px] text-gray-400 truncate max-w-[180px]">{tv.location}</div>}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {tv?.odometer_km ? (
                            <span className="text-xs text-gray-700 font-medium">
                              {Math.round(tv.odometer_km * 0.621371).toLocaleString()} mi
                            </span>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {tv?.fuel_level != null ? (
                            <div className="flex items-center justify-end gap-1.5">
                              <div className="w-10 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${tv.fuel_level < 20 ? 'bg-red-500' : tv.fuel_level < 40 ? 'bg-amber-400' : 'bg-green-500'}`}
                                  style={{ width: `${Math.min(100, tv.fuel_level)}%` }} />
                              </div>
                              <span className={`text-xs font-medium ${tv.fuel_level < 20 ? 'text-red-600' : 'text-gray-600'}`}>{tv.fuel_level}%</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5"><DateCell date={v.mot_due} /></td>
                        <td className="px-3 py-2.5"><DateCell date={v.next_pmi_due} /></td>
                        <td className="px-3 py-2.5 text-center"><StatusIndicator status={cStatus} /></td>
                        {isManager && (
                          <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-end gap-2">
                              <button onClick={() => openEdit(v)} className="text-xs text-gray-500 hover:text-gray-700">Edit</button>
                              <button onClick={() => handleDeactivate(v.id)} className="text-xs text-gray-400 hover:text-amber-600">Deactivate</button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Inactive Vehicles ──────────────────────────────────── */}
          {inactiveVehicles.length > 0 && (
            <div className="mt-4">
              <button onClick={() => setShowInactive(!showInactive)}
                className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700">
                <span className={`transition-transform text-[10px] ${showInactive ? 'rotate-90' : ''}`}>▶</span>
                Inactive Vehicles ({inactiveVehicles.length})
              </button>
              {showInactive && (
                <div className="mt-2 bg-white rounded border border-gray-200 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Registration</th>
                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Make / Model</th>
                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Year</th>
                        <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {inactiveVehicles.map(v => (
                        <tr key={v.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-700 font-medium">{v.registration}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{[v.make, v.model].filter(Boolean).join(' ') || '—'}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{v.year || '—'}</td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex justify-end gap-3">
                              <button onClick={() => handleReactivate(v.id)} className="text-xs text-green-600 hover:text-green-800">Reactivate</button>
                              <button onClick={() => handlePermanentDelete(v.id)} className="text-xs text-red-500 hover:text-red-700">Delete Permanently</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showForm && renderFormModal()}
    </PortalLayout>
  );
}
